import React from 'react';

import {useDocRevisionsContext} from '../../../../providers/version';

import styles from './document-view.module.css';
import {Editor} from './editor';
import {useEditorModel} from './model';
import {useHandleFindUsages} from '../../common/sidebar-search-field/hooks/use-handle-find-usages';
import {FindUsagesEditorEvent} from '../../../../../webeditor/biokb-editor-adapters';
import {GlobalVersionData} from '../../../../hooks/global-version/use-global-version-query';
import {
    CustomResizableLayout,
    ResizablePanel
} from '../../../../components/layout/resizable-layout';
import {NeighborhoodGraph} from './neighborhood-graph/neighborhood-graph';
import {useNeighborhoodGraphSettings} from './neighborhood-graph/hooks/use-neighborhood-graph-settings';
import {useDocLayoutSettings} from '../hooks/use-doc-layout-settings';
import {useGraphSelectionProps} from '../../../../components/graph/graph-logic/use-graph-selection-props';
import {RightPanelWrapper} from '../../common/right-sidebar/right-panel-wrapper';
import {useDocumentStatusInThisGVQuery} from '../hooks/requests/use-document-status-in-this-gv-query';
import {isReadOnlyOntology} from '../../../../utils/ontology';
import {BusyIndicator} from '../../../../components/busy-indicator';
import {isDocNotViewableInThisGV} from '../../../../utils/document-viewable-status';
import {LocalBackdrop} from '../../../../components/local-backdrop';
import {NotViewableStub} from './editor-backdrop/not-viewable-stub';
import {useDocumentSidebarState} from '../../common/right-sidebar/hooks/use-document-sidebar-state';
import {useHasOntologyTree} from '../../common/right-sidebar/hooks/use-has-ontology-tree';
import {useDocumentQuery} from '../hooks/requests/use-document-query';

interface OwnProps {
    documentId: string;
    revisionId?: string;
    findUsagesProps: ReturnType<typeof useHandleFindUsages>;
    selectedGlobalVersion: GlobalVersionData;
    documentQuery: ReturnType<typeof useDocumentQuery>;
}

export const DocumentView = ({
    documentId,
    revisionId,
    findUsagesProps,
    selectedGlobalVersion,
    documentQuery
}: OwnProps) => {
    const {revisions, selectedRevision} = useDocRevisionsContext();

    const state = useEditorModel({
        documentId,
        revisionId,
        documentInfo: documentQuery.data
    });

    const [selectedFacts, setSelectedFacts] = React.useState<string[]>([]);
    const closeNeighborhoodGraph = React.useCallback(() => setSelectedFacts([]), []);

    const handleFindUsages = React.useCallback(
        (event: FindUsagesEditorEvent) => findUsagesProps.findUsages(event, documentId, revisionId),
        [findUsagesProps, documentId, revisionId]
    );

    const neighborhoodGraphSettings = useNeighborhoodGraphSettings();
    const graphSelectionProps = useGraphSelectionProps();
    const docLayoutSettings = useDocLayoutSettings({
        selectedFacts,
        selectedNodes: graphSelectionProps.selectedNodes
    });

    const docViewableInThisGV = useDocumentStatusInThisGVQuery({
        accession: documentId,
        globalVersionId: selectedGlobalVersion.id,
        enabled: !isReadOnlyOntology(documentQuery.data)
    });

    const rightSidebarProps = useDocumentSidebarState(true);
    const hasOntologyTreeProps = useHasOntologyTree({
        isExploreView: true,
        docSidebarState: rightSidebarProps,
        docInfo: documentQuery.data
    });

    if (docViewableInThisGV.isLoading) {
        return (
            <div className={styles.documentBody}>
                <BusyIndicator />
            </div>
        );
    }

    if (!revisionId && isDocNotViewableInThisGV(docViewableInThisGV.data)) {
        return (
            <LocalBackdrop open variant="zerostate">
                <NotViewableStub
                    variant={docViewableInThisGV.data}
                    globalVersionLabel={selectedGlobalVersion.label}
                    docId={documentId}
                    earliestRevisionId={revisions && revisions[revisions.length - 1].revision}
                    latestRevisionId={revisions && revisions[0].revision}
                />
            </LocalBackdrop>
        );
    }

    return (
        <CustomResizableLayout
            localStorageKey="documents.explore.neighborhoodGraph.layoutSize"
            alignment="vertical"
            settings={{initialFirstPanelShare: 0.6, minPermittedHeight: 200}}
            fixedPanelSize={docLayoutSettings.graphPanelFixedSize}
        >
            {(resizableLayoutProps) => (
                <React.Fragment>
                    <ResizablePanel index={0} {...resizableLayoutProps}>
                        <RightPanelWrapper
                            docInfo={documentQuery.data}
                            globalVersion={selectedGlobalVersion.id}
                            rightSidebarProps={rightSidebarProps}
                            hasOntologyTreeProps={hasOntologyTreeProps}
                        >
                            <div className={styles.documentBody}>
                                <Editor
                                    documentId={documentId}
                                    revisionId={revisionId}
                                    editorState={state}
                                    handleFindUsages={handleFindUsages}
                                    selectedGlobalVersion={selectedGlobalVersion}
                                    selectedFacts={selectedFacts}
                                    onFactsSelectionChanged={
                                        !selectedRevision ? setSelectedFacts : () => null
                                    }
                                    rightSidebarProps={rightSidebarProps}
                                    docLayoutSettings={docLayoutSettings}
                                    documentQuery={documentQuery}
                                />
                            </div>
                        </RightPanelWrapper>
                    </ResizablePanel>

                    {selectedFacts.length ? (
                        <ResizablePanel index={1} {...resizableLayoutProps}>
                            <NeighborhoodGraph
                                selectedFacts={selectedFacts}
                                closeGraph={closeNeighborhoodGraph}
                                documentId={documentId}
                                globalVersionId={selectedGlobalVersion.id}
                                key={selectedFacts.join(',')}
                                graphSettings={neighborhoodGraphSettings}
                                layoutSettings={docLayoutSettings}
                                graphSelectionProps={graphSelectionProps}
                            />
                        </ResizablePanel>
                    ) : undefined}
                </React.Fragment>
            )}
        </CustomResizableLayout>
    );
};
